import { createTheme, Theme } from '@mui/material/styles';

// Soft, tinted shadows (cool navy instead of pure black) applied across all
// 25 MUI elevation levels, so every Paper/Card/Menu/Dialog picks up the
// same "soothing" feel without each component needing its own override.
const softShadow = (elevation: number): string => {
  if (elevation === 0) return 'none';
  const blur = 4 + elevation * 2.2;
  const spread = Math.round(elevation * 0.6);
  const opacity = Math.min(0.04 + elevation * 0.006, 0.18);
  return `0 ${Math.round(elevation * 0.8)}px ${Math.round(blur)}px ${spread}px rgba(15, 23, 42, ${opacity.toFixed(3)})`;
};

const shadows = Array.from({ length: 25 }, (_, i) => softShadow(i)) as Theme['shadows'];

const fontFamily = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  'Oxygen',
  'Ubuntu',
  'Cantarell',
  '"Fira Sans"',
  '"Droid Sans"',
  '"Helvetica Neue"',
  'sans-serif',
].join(',');

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0073ea',
      light: '#4facfe',
      dark: '#0060b9',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#676879',
      light: '#c5c7d0',
      contrastText: '#ffffff',
    },
    success: { main: '#22c58c', light: '#6fe3a8' },
    warning: { main: '#f5b942', light: '#ffd76f' },
    error: { main: '#f0576b', light: '#ff9a8b' },
    info: { main: '#26c6da', light: '#7de3ef' },
    background: {
      default: '#f7f8fc',
      paper: '#ffffff',
    },
    text: {
      primary: '#323338',
      secondary: '#4b5563',
    },
    divider: '#e6e9ef',
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily,
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  shadows,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarWidth: 'thin',
        },
        '*::-webkit-scrollbar': {
          width: 8,
          height: 8,
        },
        '*::-webkit-scrollbar-track': {
          background: '#f5f6f8',
        },
        '*::-webkit-scrollbar-thumb': {
          background: '#c5c7d0',
          borderRadius: 8,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: '#676879',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'transform 150ms ease-in-out, box-shadow 150ms ease-in-out, background-color 150ms ease-in-out',
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 14,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'border-color 150ms ease-in-out, box-shadow 150ms ease-in-out',
        },
        notchedOutline: {
          borderColor: '#e6e9ef',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '0.75rem',
          backgroundColor: '#323338',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: '1px solid #e6e9ef',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 115, 234, 0.08)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 8,
        },
      },
    },
  },
});

export default theme;
